<template>
  <b-modal :title="helper.trans('labels.payout_upload_files_title')"
           :modelValue="modal !== null"
           footer-class="d-flex justify-content-between"
           @update:modelValue="$event === false && (modal = null)">
    <template #default>
      <div class="row mb-3">
        <div class="col-sm-12">
          <div v-if="modal">
            <div class="text-center">
              <small class="text-danger">
                <i class="fa fa-warning"></i> {{ helper.trans('labels.payout_confirmation_warning')}}
              </small>
            </div>
            <div class="d-flex flex-column align-items-center mt-3">
              <div>
                <small class="text-capitalize text-secondary">{{helper.trans('labels.id')}}:</small> <span v-b-tooltip.hover :title="modal.deal.publicId">{{ modal.deal.publicId }}</span> <copy-button :value="modal.deal.publicId"></copy-button>
              </div>
              <div v-if="modal.deal.actualTakerSum">
                <div>
                  <small class="text-capitalize text-secondary">{{helper.trans('labels.sum')}}:</small> {{ numberFormatter.formatMoney(modal.deal.actualTakerSum) }}
                  <copy-button :value="modal.deal.actualTakerSum.amount"></copy-button>
                </div>
              </div>
              <div v-if="!modal.deal.actualTakerSum || modal.deal.isActualSumsChanged" :class="{'text-decoration-line-through': modal.deal.isActualSumsChanged}">
                <small class="text-capitalize text-secondary">{{helper.trans('labels.sum')}}:</small> {{ numberFormatter.formatMoney(modal.deal.takerSum) }}
                <copy-button :value="modal.deal.takerSum.amount"></copy-button>
              </div>
            </div>
            <div class="d-flex flex-row align-items-center gap-2 justify-content-center mt-3">
              <PayMethodIcon :method="modal.deal.paymentMethod"></PayMethodIcon>
              <div class="d-flex flex-column">
                <small class=""> {{ helper.trans('payment_options.' + modal.deal.paymentOption) }} {{ helper.trans('pay_methods.' + modal.deal.paymentMethod) }}</small>
                <div v-if="modal.deal.type === Direction.PAY_OUT && modal.deal.status === DealStatus.NEW">
                  <small class="text-secondary text-wrap">{{ helper.trans('labels.accept_deal_to_show_req')}}</small>
                </div>
                <div v-else class="d-flex flex-column">
                            <span>
                              <span v-if="modal.deal.paymentOption === PaymentOption.TO_CARD">
                                {{ modal.deal.paymentRequisites.requisites.replace(/(\d{4})/g, '$1 ').trim() }}
                              </span>
                              <span v-else>
                                {{ modal.deal.paymentRequisites.requisites }}
                              </span>
                            <copy-button class="ms-1" :value="modal.deal.paymentRequisites.requisites"></copy-button>
                          </span>
                  <small class="text-secondary"> {{ modal.deal.paymentRequisites.holder }}</small>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
      <div class="row mb-3">
        <div class="col-sm-12">
          <div style="cursor: pointer; border:1px dashed" v-bind="dropzone.getRootProps()" class="payout-dropzone rounded border-secondary p-3 pt-4 pb-4 d-flex justify-content-center align-items-center">
            <input v-bind="dropzone.getInputProps()" />
            <p class="p-0 m-0">{{ helper.trans('labels.payout_upload_files')}}</p>
          </div>
        </div>
      </div>
      <div v-for="fileAttachment in fileAttachments" class="row mb-2 ms-2" :key="fileAttachment.id">
        <div class="col-sm-12">
          <i class="fa fa-paperclip"></i> <span class="me-2">{{ fileAttachment.file.name}}</span>
          <div v-if="fileAttachment.error === null && fileAttachment.attachment === null" class="spinner-border" role="status" style="zoom: 0.5">
            <span class="visually-hidden"></span>
          </div>
          <a v-else href="#" @click.prevent="removeFile(fileAttachment)">
            <i class="fa fa-times"></i>
          </a>
          <div>
            <small class="text-danger" v-if="fileAttachment.error !== null">{{fileAttachment.error}}</small>
          </div>
        </div>
        <div class="col-sm-12">
          <canvas :id="`pdf-canvas-${fileAttachment.id}`" class="w-100"></canvas>
        </div>
      </div>
    </template>
    <template #footer>
      <button type="button" @click="modal = null" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ modal?.cancelTitle ?? helper.trans('button.cancel')  }}
      </button>
      <LoaderWrapper ref="loader" :zoom="0.5">
        <button type="button" @click="onOkClick()" class="btn btn-outline-danger btn-sm text-capitalize-first">
          {{ modal?.okTitle ?? helper.trans('button.ok') }}
        </button>
      </LoaderWrapper>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import { useDropzone } from "vue3-dropzone";
import APIClient from "@/service/APIClient";
import AttachmentDTO from "@/models/AttachmentDTO";
import {useNotification} from "@kyvg/vue3-notification";
import TraderDealDTO from "@/models/TraderDealDTO";
import {Direction} from "@/models/enum/Direction";
import {DealStatus} from "@/models/enum/DealStatus";
import CopyButton from "@/components/CopyButton.vue";
import {numberFormatter} from "@/service/NumberFormatter";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {PaymentOption} from "@/models/enum/PaymentOption";

export interface PayoutConfirmModalProps {
  okTitle: string|null,
  deal: TraderDealDTO,
  cancelTitle: string|null,
  onOkClick(): void
}

interface AttachmentFileDTO {
  id: number;
  attachment: AttachmentDTO|null,
  file: File,
  error: string|null
}

const api = new APIClient();
const notification = useNotification()
let id = 1;

export default defineComponent({
  components: {
    PayMethodIcon,
    BModal,
    LoaderWrapper,
    CopyButton
  },
  setup() {

    window.pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      modal: null as PayoutConfirmModalProps|null,
      fileAttachments: [] as AttachmentFileDTO[],
    });

    const getBase64 = (attachment:AttachmentFileDTO) => {
      var reader = new FileReader();
      reader.readAsDataURL(attachment.file);
      reader.onload = () => {
        if (!reader.result) {
          return;
        }

        const pdfData = atob(reader.result.toString().replace('data:application/pdf;base64,', ''))
        const loadingTask = window.pdfjsLib.getDocument({data: pdfData});
        loadingTask.promise.then((pdf:any) => {
          pdf.getPage(1).then((page:any) => {
            const scale = 1.5;
            const viewport = page.getViewport({scale: scale});

            const canvas = document.getElementById('pdf-canvas-' + attachment.id) as HTMLCanvasElement
            if (!canvas) {
              return;
            }
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const renderTask = page.render({
              canvasContext: context,
              viewport: viewport
            });
            renderTask.promise.then(() => {
            });
          });
        }, (reason: any) => {
          console.error(reason);
        });
      };
      reader.onerror = error => {};
    }

    const onDrop = async (acceptFiles: any, rejectReasons: any) => {
      for (const file of acceptFiles) {
        state.fileAttachments.push({
          id: id++,
          attachment: null,
          file,
          error: null
        } as AttachmentFileDTO)
      }

      for (const fileAttachment of state.fileAttachments) {
        try {
          try {
            getBase64(fileAttachment)
          } catch (e) {}
          fileAttachment.attachment = await api.attachmentsUpload(fileAttachment.file)
        } catch (e) {
          if (helper.isValidationErrors(e)) {
            for (const item of helper.getValidationErrors(e)) {
              notification.notify({type: 'error', title: helper.trans(`server_errors.${item.code}`, {}, item.message)});
            }
          } else {
            notification.notify({type: 'error', title: helper.trans(`server_errors.c8c7315c-6186-4719-8b71-5659e16bdcb7`)});
          }
          fileAttachment.error = helper.trans(`server_errors.c8c7315c-6186-4719-8b71-5659e16bdcb7`)
        }

      }
    }
    const dropzone = useDropzone({ onDrop, accept:['.pdf'] });

    const getForm = () => {
      return {
        attachments: state.fileAttachments.filter((file) => file.attachment !== null).map((file) => file.attachment!.id)
      }
    }

    const show = (props: PayoutConfirmModalProps) => {
      state.modal = props
      state.fileAttachments = [];
    }

    const removeFile = (fileAttachment: AttachmentFileDTO) => {
      state.fileAttachments = state.fileAttachments.filter((file) => file.id !== fileAttachment.id)
    }

    const onOkClick = async () => {
      if (!state.modal) {
        return;
      }

      loader.value?.show()
      try {
        await state.modal.onOkClick();
      } finally {
        loader.value?.hide()
      }

      state.modal = null;
    }

    return {
      ...toRefs(state),
      onOkClick,
      show,
      loader,
      helper,
      dropzone,
      getForm,
      removeFile,
      Direction,
      DealStatus,
      numberFormatter,
      PaymentOption
    }
  }
})
</script>
